import { useEffect, useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useIntl } from 'react-intl';
import { useLoaderData } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { useBaseMap } from 'client/pages/MapPage/layers/sources/BaseMap.js';
import { Attribution, Controls, Layers, Map, ScaleLine, TileLayer, View, Zoom, VectorLayer } from 'os-elements';
import VectorSource from 'ol/source/Vector';
import Polygon from 'ol/geom/Polygon';
import Feature from 'ol/Feature';
import PhaseBanner from 'client/components/suc/PhaseBanner/PhaseBanner';
import MapMenu from '../MapPage/components/MapMenu/MapMenu';
import MapLoading from '../MapPage/components/MapLoading/MapLoading';
import SnackBars from 'client/components/ui/SnackBars/SnackBars';
import SnackBar from 'client/components/ui/Snackbar/SnackBar';
import MapInfo from '../MapPage/components/MapInfo/MapInfo';
import OSBranding from '../MapPage/components/OSBranding/OSBranding';
import styles from '../MapPage/MapPage.module.scss';
import networkCoverageStyle from '../MapPage/layers/styles/networkCoverageStyle';
import NetworkCoverageSnackBar from '../MapPage/components/SnackBars/NetworkCoverageSnackBar/NetworkCoverageSnackBar';
import { mapMenuTabKeys } from 'client/utils/constants/appStates';
import { setMenuOpen } from 'client/store/mapSlice/mapSlice';
import { selectMessagesByLocation } from 'client/store/systemMessageSlice/systemMessageSlice';
import NIBranding from '../MapPage/components/NIBranding/NIBranding';
import { selectMyCoverage } from 'client/store/coverageAssetOwnersSlice/coverageAssetOwnersSlice';
import { observeSystemMessageHeight } from 'client/utils/helpers/SystemMessageHeight/SystemMessageHeight';

const NetworkCoveragePage = () => {
    const { epsgCode } = useLoaderData() || {};
    const intl = useIntl();
    const myOperationalAreasId = 'myOperationalAreas';
    const baseMapSource = useBaseMap();
    const mapView = useSelector(state => state.mapView?.result?.mapView);
    const myCoverage = useSelector(selectMyCoverage, shallowEqual);
    const coverageMessages = useSelector(selectMessagesByLocation('coverage'), shallowEqual);
    const hasMessages = coverageMessages.length > 0;
    const dispatch = useDispatch();
    const [coverageSystemMessageHeight, setCoverageSystemMessageHeight] = useState(0);

    useEffect(() => {
        dispatch(setMenuOpen(true));
    }, [dispatch]);

    useEffect(() => {
        if (!hasMessages) {
            setCoverageSystemMessageHeight(0);
            return undefined;
        }

        return observeSystemMessageHeight({
            location: 'coverage',
            onUpdate: setCoverageSystemMessageHeight
        });
    }, [coverageMessages, hasMessages]);

    function convertServiceAreaToFeature (serviceAreas) {
        const features = [];
        const isNested = coords => Array.isArray(coords[0][0][0]);

        serviceAreas.forEach(area => {
            let coords = area.geometry.coordinates;

            if (!isNested(coords)) {
                coords = [coords];
            }

            coords.forEach(nested => {
                const feature = new Feature({
                    geometry: new Polygon(nested)
                });
                features.push(feature);
            });
        });
        return features;
    }
    const regions = convertServiceAreaToFeature(myCoverage?.serviceAreas || []);

    return (
        <div
            role='main'
            data-testid="networkCoveragePage"
            className={`${styles.page} ${hasMessages ? styles.withMessages : ''}`}
            style={{ '--map-system-message-height': `${coverageSystemMessageHeight}px` }}
        >
            <Helmet>
                <meta charSet="utf-8" />
                <title>{intl.formatMessage({ id: 'networkCoveragePage.title' })}</title>
            </Helmet>
            <div className={styles.phaseBanner}>
                <PhaseBanner />
            </div>
            <div className={styles.container}>
                <MapMenu filterKey={mapMenuTabKeys.COVERAGE_ASSET_OWNERS} />
                <div className={styles.map}>
                    <MapLoading />
                    <SnackBars>
                        {alert?.open && <SnackBar variant={alert?.severity}>{alert?.content}</SnackBar>}
                        <NetworkCoverageSnackBar />
                    </SnackBars>
                    <Map>
                        <View {...mapView} showFullExtent={true} enableRotation={false} />
                        <Controls>
                            <Zoom />
                            <ScaleLine />
                            <Attribution />
                            {epsgCode === '27700' && <OSBranding />}
                            {epsgCode === '29903' && <NIBranding />}
                        </Controls>
                        <Layers>
                            {baseMapSource && <TileLayer id="Base Map" source={baseMapSource} visible zIndex={0} />}
                            <VectorLayer
                                id="myOperationalAreaPolygon"
                                visible={true}
                                source={new VectorSource({ features: regions })}
                                zIndex={1}
                                style={feature => networkCoverageStyle(myOperationalAreasId, feature)}
                            />
                        </Layers>
                    </Map>
                    <MapInfo />
                </div>
            </div>
        </div>
    );
};

export default NetworkCoveragePage;
